import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck, GraduationCap, UserCog } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { UserAvatar } from './UserAvatar';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import { roleBadgeClass } from '../lib/api';

export const TopBar = () => {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const role = user?.role || 'player';

  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[rgba(18,18,18,0.78)] backdrop-blur-xl">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2" data-testid="topbar-brand">
          <img src="/logo-icon.png" alt="SkillSync AI" className="w-9 h-9 object-contain" draggable={false} data-testid="topbar-logo" />
          <span className="font-display font-bold tracking-tight text-white">SkillSync <span className="text-[#22C55E]">AI</span></span>
        </button>

        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-[14px] border border-white/10 bg-white/[0.04] p-0.5" data-testid="language-toggle">
            {['en', 'it'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`language-toggle-${l}`}
                className={`px-2.5 py-1 rounded-[12px] text-xs font-mono uppercase transition-colors ${
                  lang === l ? 'bg-white/[0.14] text-white border border-white/25' : 'text-white/45 hover:text-white/70'
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="topbar-avatar-button" className="rounded-[14px] flex items-center gap-1.5">
                <UserAvatar src={user?.picture} alt={user?.name} size={32} rounded={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1E1E1E] border-white/10 rounded-[16px] w-56">
              <DropdownMenuLabel>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate" data-testid="topbar-user-name">{user?.name}</div>
                    <div className="text-xs text-white/50 font-mono truncate">{user?.email}</div>
                  </div>
                  <span className={`shrink-0 border rounded-[10px] px-1.5 py-0.5 text-[10px] font-mono uppercase ${roleBadgeClass(role)}`} data-testid="topbar-role-badge">
                    {t('roleBadge')[role]}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-profile-settings" className="cursor-pointer text-white/85">
                <UserCog size={14} className="mr-2" /> {t('profileSettings')}
              </DropdownMenuItem>
              {role === 'player' && (
                <DropdownMenuItem onClick={() => navigate('/become-coach')} data-testid="menu-become-coach" className="cursor-pointer text-white/85">
                  <GraduationCap size={14} className="mr-2" /> {t('becomeCoach')}
                </DropdownMenuItem>
              )}
              {(role === 'coach' || role === 'admin') && (
                <DropdownMenuItem onClick={() => navigate('/coach')} data-testid="menu-coach-studio" className="cursor-pointer text-white/85">
                  <GraduationCap size={14} className="mr-2" /> {t('coachArea')}
                </DropdownMenuItem>
              )}
              {role === 'admin' && (
                <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin" className="cursor-pointer text-white/85">
                  <ShieldCheck size={14} className="mr-2" /> {t('adminTitle')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-[#E76F7B] focus:text-[#E76F7B] cursor-pointer">
                <LogOut size={14} className="mr-2" /> {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
